import {
  AppEventTypes,
  BUXUS_GA4_WEB_APP_MEASUREMENT_ID,
  BUXUS_MIXPANEL_PROJECT_TOKEN,
  buildGaStyleScreenName,
  buildWebAnalyticsContext,
  isPageViewedEvent,
  mergeAnalyticsContext,
  normalizeGeoCoordAnalyticsProperties,
  resolveWebReleaseStage,
} from '@buxus/shared'
import mixpanel from 'mixpanel-browser'

let mixpanelReady = false
let analyticsInitialized = false
/** Browser offline state — kept as Mixpanel superprop + merged onto each event. */
let isOffline =
  typeof navigator !== 'undefined' ? navigator.onLine === false : false

function webReleaseStage() {
  return resolveWebReleaseStage({
    viteMode: import.meta.env.MODE,
    envOverride: import.meta.env.VITE_RELEASE_STAGE,
  })
}

function analyticsContext(): Record<string, unknown> {
  return buildWebAnalyticsContext({ releaseStage: webReleaseStage() })
}

let currentTenantId: string | null = null
let currentTenantName: string | null = null
let currentSessionId: string | null = null

function tenantSuperProperties(): Record<string, unknown> {
  return {
    ...(currentTenantId ? { tenant_id: currentTenantId } : {}),
    ...(currentTenantName ? { tenant_name: currentTenantName } : {}),
  }
}

function sessionProperties(): Record<string, unknown> {
  return currentSessionId ? { sessionId: currentSessionId } : {}
}

function registerMixpanelSuperProperties(): void {
  if (!mixpanelReady) return
  mixpanel.register({
    ...analyticsContext(),
    current_stakeholder_type: currentStakeholderType,
    is_offline: isOffline,
    ...tenantSuperProperties(),
    ...sessionProperties(),
  })
}

function syncBrowserOnlineStatus(): void {
  if (typeof navigator === 'undefined') return
  const next = navigator.onLine === false
  if (next === isOffline) return
  isOffline = next
  registerMixpanelSuperProperties()
}

let currentStakeholderType: string | null = null

function loadGtag(measurementId: string): void {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer ?? []
  if (!window.gtag) {
    // Must push `arguments`, not a rest-param array — gtag.js only processes the former.
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments)
    } as Window['gtag']
  }
  const gtag = window.gtag!
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`)) return
  gtag('js', new Date())
  gtag('config', measurementId)
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(script)
}

/** GA4 custom dims: primitives + short JSON for nested legacy payloads. */
function toGtagParams(props: Record<string, unknown>): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  for (const [k, v] of Object.entries(props)) {
    const key = k.length <= 40 ? k : k.slice(0, 40)
    if (v === null || v === undefined) continue
    if (typeof v === 'string') {
      out[key] = v.length > 500 ? `${v.slice(0, 497)}...` : v
    } else if (typeof v === 'number' && Number.isFinite(v)) {
      out[key] = v
    } else if (typeof v === 'boolean') {
      out[key] = v ? 1 : 0
    } else {
      try {
        const s = JSON.stringify(v)
        out[key] = s.length > 500 ? `${s.slice(0, 497)}...` : s
      } catch {
        out[key] = '[unserializable]'
      }
    }
  }
  return out
}

function resolveMixpanelToken(): string | undefined {
  return import.meta.env.VITE_MIXPANEL_TOKEN?.trim() || BUXUS_MIXPANEL_PROJECT_TOKEN
}

function resolveGaMeasurementId(): string | undefined {
  return import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() || BUXUS_GA4_WEB_APP_MEASUREMENT_ID
}

/**
 * Call once at startup. Uses `VITE_MIXPANEL_TOKEN` / `VITE_GA_MEASUREMENT_ID` when set,
 * otherwise falls back to {@link BUXUS_MIXPANEL_PROJECT_TOKEN} / {@link BUXUS_GA4_WEB_APP_MEASUREMENT_ID}.
 */
export function initAnalytics(): void {
  if (analyticsInitialized) return
  analyticsInitialized = true

  const mp = resolveMixpanelToken()
  if (mp) {
    mixpanel.init(mp, {
      // Console dumps only — does not disable Mixpanel tracking. Opt in for local debugging:
      // VITE_MIXPANEL_DEBUG=true
      debug: import.meta.env.VITE_MIXPANEL_DEBUG === 'true',
      track_pageview: false,
      persistence: 'localStorage',
      // Offline durability: queue events and flush when connectivity returns (default true;
      // set explicitly — reliability / network_request_failed must not use send_immediately).
      batch_requests: true,
      ignore_dnt: true,
    })
    mixpanelReady = true
    if (typeof window !== 'undefined') {
      window.addEventListener('online', syncBrowserOnlineStatus)
      window.addEventListener('offline', syncBrowserOnlineStatus)
      syncBrowserOnlineStatus()
    }
    registerMixpanelSuperProperties()
  }

  const gaId = resolveGaMeasurementId()
  if (gaId) {
    loadGtag(gaId)
  }
}

/** @deprecated Use {@link initAnalytics}. */
export const initWebAnalytics = initAnalytics

export function registerAnalyticsStakeholderType(stakeholderType: string | null | undefined): void {
  currentStakeholderType = stakeholderType?.trim() || null
  registerMixpanelSuperProperties()
}

export function registerAnalyticsTenant(opts: {
  tenantId?: string | null
  tenantName?: string | null
}): void {
  currentTenantId = opts.tenantId?.trim() || null
  currentTenantName = opts.tenantName?.trim() || null
  registerMixpanelSuperProperties()
}

/** Board in the address bar / open session. Cleared on the list and on logout. */
export function registerAnalyticsSession(opts: { sessionId?: string | null }): void {
  const next = opts.sessionId?.trim() || null
  if (next === currentSessionId) return
  currentSessionId = next
  if (mixpanelReady && !next) mixpanel.unregister('sessionId')
  registerMixpanelSuperProperties()
}

export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
  options?: { sendImmediately?: boolean },
): void {
  const normalized = normalizeGeoCoordAnalyticsProperties(properties) ?? {}
  // Merge onto the event payload (not only Mixpanel super-props) so raw exports always
  // include release_stage / app_surface even when super-property registration races.
  const payload = mergeAnalyticsContext(normalized, {
    ...analyticsContext(),
    current_stakeholder_type: currentStakeholderType,
    is_offline: isOffline,
    ...tenantSuperProperties(),
    ...(normalized.sessionId ? {} : sessionProperties()),
  })

  if (mixpanelReady) {
    registerMixpanelSuperProperties()
    if (options?.sendImmediately) {
      mixpanel.track(eventName, payload, { send_immediately: true })
    } else {
      mixpanel.track(eventName, payload)
    }
  } else if (import.meta.env.DEV) {
    console.debug('[analytics]', eventName, payload)
  }

  if (typeof window !== 'undefined' && window.gtag) {
    if (isPageViewedEvent(eventName)) {
      const screenName = buildGaStyleScreenName(payload as Parameters<typeof buildGaStyleScreenName>[0])
      window.gtag('event', 'screen_view', toGtagParams({ screen_name: screenName, ...payload }))
    }
    window.gtag('event', eventName, toGtagParams(payload))
  }
}

/** @deprecated Use {@link trackEvent}. */
export const trackWebEvent = trackEvent

export function identifyUser(userId: string): void {
  if (!mixpanelReady) return
  if (typeof userId !== 'string' || !userId.trim()) return
  mixpanel.identify(userId.trim())
}

/** @deprecated Use {@link identifyUser}. */
export const identifyWebUser = identifyUser

export function setUserProfile(properties: Record<string, unknown>): void {
  if (!mixpanelReady) return
  mixpanel.people.set(properties)
}

/** @deprecated Use {@link setUserProfile}. */
export const setWebUserProfile = setUserProfile

export function resetAnalyticsUser(): void {
  if (!mixpanelReady) return
  mixpanel.reset()
  currentStakeholderType = null
  currentTenantId = null
  currentTenantName = null
  currentSessionId = null
}

export { AppEventTypes }
